/**
 * Utility for persisting and restoring context selector state within the current session
 * Uses sessionStorage so state is cleared when the browser tab/window is closed
 */
const STORAGE_KEY = "ps-context-selector-state";
const defaultState = {
    searchText: "",
    activeTab: "accounts",
    isExpanded: false,
    lastUsed: new Date().toISOString(),
};
export class ContextSelectorStateStorage {
    /**
     * Get the saved state from sessionStorage (session-only, not persistent across page loads)
     */
    static getState() {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            if (!stored) {
                return defaultState;
            }
            const parsed = JSON.parse(stored);
            // Validate the parsed state has required properties
            if (!parsed.activeTab || !["accounts", "users", "privilegeGroups"].includes(parsed.activeTab)) {
                return defaultState;
            }
            return {
                searchText: parsed.searchText || "",
                activeTab: parsed.activeTab,
                isExpanded: Boolean(parsed.isExpanded),
                lastUsed: parsed.lastUsed || new Date().toISOString(),
            };
        }
        catch (_error) {
            // console.warn("Failed to load context selector state:", _error);
            return defaultState;
        }
    }
    /**
     * Save the current state to sessionStorage (session-only, not persistent across page loads)
     */
    static saveState(state) {
        try {
            const currentState = this.getState();
            const newState = {
                ...currentState,
                ...state,
                lastUsed: new Date().toISOString(),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
        }
        catch (_error) {
            // console.warn("Failed to save context selector state:", _error);
        }
    }
    /**
     * Clear the saved state
     */
    static clearState() {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        }
        catch (_error) {
            // console.warn("Failed to clear context selector state:", _error);
        }
    }
    /**
     * Check if the saved state exists and is recent (within the current session)
     * Since we use sessionStorage, this mainly checks if state exists
     */
    static isStateRecent() {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            if (!stored) {
                return false;
            }
            const state = this.getState();
            const lastUsed = new Date(state.lastUsed);
            const now = new Date();
            const hoursDiff = (now.getTime() - lastUsed.getTime()) / (1000 * 60 * 60);
            // State is recent if less than 1 hour old (within current session)
            return hoursDiff < 1;
        }
        catch (_error) {
            return false;
        }
    }
}
